/**
 * Service for managing weapon and armor proficiencies
 */

import type { Talent } from '../types/character';
import type {
  CharacterProficiencies,
  ShopItemWithProficiency,
  ProficiencyPenalty
} from '../types/proficiency';
import { CyberneticsService } from './CyberneticsService';

export class ProficiencyService {
  /**
   * Calculate all proficiencies from character talents
   */
  static calculateProficiencies(talents: Talent[]): CharacterProficiencies {
    const proficiencies: CharacterProficiencies = {
      weapons: {
        types: [],
        classes: [],
        universal: [],
        exotic: []
      },
      armor: []
    };

    for (const talent of talents) {
      // Parse weapon training talents
      if (talent.baseTalent === 'Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('All');
        } else if (talent.specialization) {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      // Parse class-specific training
      if (talent.baseTalent === 'Melee Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('Melee');
        } else if (talent.specialization && talent.specialization !== 'any') {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      if (talent.baseTalent === 'Pistol Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('Pistol');
        } else if (talent.specialization && talent.specialization !== 'any') {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      if (talent.baseTalent === 'Basic Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('Basic');
        } else if (talent.specialization && talent.specialization !== 'any') {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      if (talent.baseTalent === 'Heavy Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('Heavy');
        } else if (talent.specialization && talent.specialization !== 'any') {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      if (talent.baseTalent === 'Thrown Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('Thrown');
        } else if (talent.specialization && talent.specialization !== 'any') {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      if (talent.baseTalent === 'Flame Weapon Training') {
        if (talent.specialization === 'Universal') {
          proficiencies.weapons.universal.push('Flame');
        } else if (talent.specialization && talent.specialization !== 'any') {
          proficiencies.weapons.types.push(talent.specialization);
        }
      }

      // Exotic weapons
      if (talent.baseTalent === 'Exotic Weapon Training') {
        if (talent.specialization && talent.specialization !== 'Choose One') {
          proficiencies.weapons.exotic.push(talent.specialization);
        }
      }

      // Armor training
      if (talent.baseTalent === 'Armor Training' && talent.specialization) {
        proficiencies.armor.push(talent.specialization);
      }
    }

    // Remove duplicates
    proficiencies.weapons.types = [...new Set(proficiencies.weapons.types)];
    proficiencies.weapons.classes = [...new Set(proficiencies.weapons.classes)];
    proficiencies.weapons.universal = [...new Set(proficiencies.weapons.universal)];
    proficiencies.weapons.exotic = [...new Set(proficiencies.weapons.exotic)];
    proficiencies.armor = [...new Set(proficiencies.armor)];

    return proficiencies;
  }

  /**
   * Check if character is proficient with a weapon.
   * For mechadendrite weapons, checks Mechadendrite Use talent instead of Weapon Training.
   * Pass `talents` to enable mechadendrite proficiency detection.
   */
  static isProficientWithWeapon(
    item: ShopItemWithProficiency,
    proficiencies: CharacterProficiencies,
    talents?: Talent[]
  ): boolean {
    // Gear items don't require proficiency
    if (item.type !== 'weapon') return true;

    // Check if this is a mechadendrite weapon (uses sourceId to identify)
    if (
      item.sourceId &&
      item.sourceId in CyberneticsService.DEFINITIONS &&
      CyberneticsService.DEFINITIONS[item.sourceId]?.proficiencyCategory
    ) {
      // Mechadendrite weapons use Mechadendrite Use talent, not Weapon Training
      if (talents) {
        return CyberneticsService.hasMechadendriteUseTalent(item.sourceId, talents);
      }
      // Without talents array, we can't check mechadendrite proficiency — default to unproficient
      return false;
    }

    // No proficiency required
    if (!item.proficiencyRequired) return true;

    // Check universal proficiency (all weapons)
    if (proficiencies.weapons.universal.includes('All')) {
      return true;
    }

    // Check class-specific universal proficiency
    if (item.weaponClass) {
      const classCapitalized = item.weaponClass.charAt(0).toUpperCase() + item.weaponClass.slice(1);
      if (proficiencies.weapons.universal.includes(classCapitalized)) {
        return true;
      }
    }

    // Check specific weapon type proficiency
    if (proficiencies.weapons.types.includes(item.proficiencyRequired)) {
      return true;
    }

    // Check exotic weapons by sourceId or name (proficiency stores display name from talent specialization)
    if (item.proficiencyRequired === 'Exotic') {
      const exoticList = proficiencies.weapons.exotic;
      if (
        exoticList.includes(item.sourceId || '') ||
        exoticList.includes(item.id) ||
        exoticList.includes(item.name)
      ) {
        return true;
      }
    }

    return false;
  }
  /**
   * Check if character is proficient with armor
   */
  static isProficientWithArmor(
    item: ShopItemWithProficiency,
    proficiencies: CharacterProficiencies
  ): boolean {
    // Non-armor items don't require proficiency
    if (item.type !== 'armor') return true;

    // No proficiency required
    if (!item.proficiencyRequired) return true;

    return proficiencies.armor.includes(item.proficiencyRequired);
  }

  /**
   * Check if character is proficient with any equipment
   */
  static isProficient(
    item: ShopItemWithProficiency,
    proficiencies: CharacterProficiencies,
    talents?: Talent[]
  ): boolean {
    if (item.type === 'weapon') {
      return this.isProficientWithWeapon(item, proficiencies, talents);
    }
    if (item.type === 'armor') {
      return this.isProficientWithArmor(item, proficiencies);
    }
    return true;
  }

  /**
   * Get penalty for using equipment without proficiency
   */
  private static getProficiencyPenalty(item: ShopItemWithProficiency): ProficiencyPenalty | null {
    if (item.type === 'weapon') {
      return {
        description: 'Using weapon without proficiency',
        modifier: -20,
        affectedTests: ['BS', 'WS']
      };
    }
    if (item.type === 'armor') {
      return {
        description: 'Wearing armor without proficiency',
        modifier: -20,
        affectedTests: ['Agility']
      };
    }
    return null;
  }

  /**
   * Get human-readable penalty description
   */
  static getProficiencyPenaltyDescription(item: ShopItemWithProficiency): string {
    const penalty = this.getProficiencyPenalty(item);
    if (!penalty) return '';

    if (item.type === 'weapon') {
      return '-20 to all tests with this weapon';
    }
    if (item.type === 'armor') {
      return '-20 to Agility-based tests while wearing this armor';
    }
    return '';
  }

  /**
   * Get list of talents that would grant proficiency with an item
   */
  private static getSuggestedTalents(item: ShopItemWithProficiency): string[] {
    const suggestions: string[] = [];

    if (item.type === 'weapon' && item.proficiencyRequired) {
      // Specific weapon type training
      suggestions.push(`Weapon Training (${item.proficiencyRequired})`);

      // Class-specific universal training
      if (item.weaponClass) {
        const classCapitalized =
          item.weaponClass.charAt(0).toUpperCase() + item.weaponClass.slice(1);
        suggestions.push(`${classCapitalized} Weapon Training (Universal)`);
      }

      // Universal weapon training
      suggestions.push('Weapon Training (Universal)');
    }

    if (item.type === 'armor' && item.proficiencyRequired) {
      suggestions.push(`Armor Training (${item.proficiencyRequired})`);
    }

    return suggestions;
  }

  /**
   * Get proficiency status summary for display
   */
  static getProficiencyStatus(
    item: ShopItemWithProficiency,
    proficiencies: CharacterProficiencies,
    talents?: Talent[]
  ): {
    isProficient: boolean;
    required: string | null;
    penalty: string | null;
    suggestions: string[];
  } {
    const isProficient = this.isProficient(item, proficiencies, talents);

    return {
      isProficient,
      required: item.proficiencyRequired || null,
      penalty: isProficient ? null : this.getProficiencyPenaltyDescription(item),
      suggestions: isProficient ? [] : this.getSuggestedTalents(item)
    };
  }
}
